import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, Camera, Download, Loader2, MapPin } from "lucide-react";
import api from "lib/api";
import { useI18n } from "i18n";
import ShareButton from "components/ShareButton";
import {
  eventShareDescription,
  formatEventDates,
  formatEventPlace,
} from "lib/events";
import { usePageMeta } from "lib/meta";

/**
 * Foto isolada (`/photo/:id`) — o destino de um link compartilhado.
 *
 * A MESMA URL responde diferente conforme quem pede: para uma pessoa o nginx
 * entrega este SPA; para um crawler de rede social ele desvia ao backend, que
 * renderiza as meta tags `og:image` / `twitter:image:src` daquela foto
 * (ver `photoe/views/public.py:PhotoShareView`).
 */
export default function PhotoView() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/api/public/photos/${id}/`);
        if (active) setPhoto(res.data);
      } catch {
        if (active) setPhoto(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  // A previa desta rota e a PROPRIA foto, nao o cartao do evento — e a foto
  // que a pessoa quis compartilhar. O texto identifica de que evento ela e.
  usePageMeta(
    photo && {
      title: photo.event?.name,
      description: eventShareDescription(photo.event, lang),
      image: photo.image_url,
      type: "article",
    }
  );

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
      </div>
    );
  }

  if (!photo) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center gap-4 px-4 py-24 text-center">
        <Camera size={36} className="text-neutral-300" />
        <h1 className="text-xl font-semibold text-neutral-900">
          {t("public.photo.notFound")}
        </h1>
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-600"
        >
          <ArrowLeft size={16} />
          {t("public.event.backToEvents")}
        </Link>
      </div>
    );
  }

  const event = photo.event || {};
  const place = formatEventPlace(event);

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6">
      <Link
        to={`/events/${event.id}`}
        className="inline-flex items-center gap-2 text-sm font-medium text-neutral-500 transition-colors hover:text-neutral-900"
      >
        <ArrowLeft size={16} />
        {t("public.photo.backToEvent")}
      </Link>

      <div className="mt-4 overflow-hidden rounded-2xl bg-neutral-900">
        <img
          src={photo.image_url}
          alt={event.name || ""}
          className="mx-auto max-h-[75vh] w-auto object-contain"
        />
      </div>

      <div className="mt-5 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-neutral-900">{event.name}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-neutral-500">
            <span className="flex items-center gap-1.5">
              <CalendarDays size={14} />
              {formatEventDates(event, lang)}
            </span>
            {place && (
              <span className="flex items-center gap-1.5">
                <MapPin size={14} />
                {place}
              </span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <a
            href={photo.image_url}
            download
            className="inline-flex items-center gap-2 rounded-full border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
          >
            <Download size={16} />
            {t("public.photo.download")}
          </a>
          <ShareButton photoId={photo.id} title={event.name} />
        </div>
      </div>
    </div>
  );
}
